import autobind from 'autobind-decorator';
import Cookies from 'js-cookie';
import {configure, observable, action, computed} from 'mobx';

import {getVisitorUuid, Tracker} from '@udemy/event-tracking';
import {CookieConsent} from '@udemy/onetrust';
import {serverOrClient} from '@udemy/shared-utils';
import {AnyObject} from '@udemy/shared-utils/types';
import {UDData} from '@udemy/ud-data';

import {ExternalLeadFormSubmitEvent} from 'src/events/custom-external-events';

import {getFormIdByPriceCountry} from '../../features/demo-form/marketo-form-configuration';
import {
    AUTO_MKTO_FIELDS,
    UNKNOWN_MKTO_FIELDS,
    UTM_QUERY_PARAM_MKTO_FIELDS,
    UTM_MKTO_FIELDS,
    ERROR_STATES,
    REDIRECT_QUERY_PARAM,
    marketoCountryNames,
} from '../../types/marketo/constants';
import {MarketoField} from '../../types/marketo/marketo-field.mobx-model';
import {MarketoSelectField} from '../../types/marketo/marketo-select-field.mobx-model';
import {
    businessEmailValidator,
    genericEmailValidator,
    phoneNumberValidator,
} from '../../types/marketo/validators';
import {marketoLoaders} from '../../utils/ud-external-loaders';
import {UFBFormActionClickEvent} from '../events/events';
import {fireDemoRequestSuccessEvent} from './gtm/demo-form-gtm-events';

// This allows Jest mocking of isValid getter annotated with computed
configure({computedConfigurable: true});

export interface MarketoForms2 extends HTMLFormElement {
    addHiddenFields(fields: AnyObject): void;
    onSuccess(cb: (values: unknown, followUpUrl: string) => void): void;
}

export interface MarketoFormFields {
    firstName: MarketoField;
    lastName: MarketoField;
    email: MarketoField;
    phone: MarketoField;
    country: MarketoSelectField;
    companyName: MarketoField;
    companySize: MarketoSelectField;
    companyEmployeeSize: MarketoSelectField;
    jobTitle: MarketoField;
    jobLevel: MarketoSelectField;
    trainingNeeds?: MarketoField;
}

export class RequestDemoStore {
    @observable isMarketoFormLoaded = false;
    @observable isFormSubmitting = false;

    // The hidden MktoForms2 instance matching the formId
    marketoForm?: MarketoForms2;

    formId?: number;

    // Because fields have so much complexity, each field has its own MarketoField instance
    formFields: MarketoFormFields = {
        firstName: new MarketoField(gettext('First Name'), 'FirstName'),
        lastName: new MarketoField(gettext('Last Name'), 'LastName'),
        email: new MarketoField(gettext('Work Email'), 'Email', {type: 'email'}),
        phone: new MarketoField(gettext('Phone Number'), 'Phone'),
        country: new MarketoSelectField(
            gettext('Where are you located'),
            'Person_Country__c',
            marketoCountryNames,
        ),
        companyName: new MarketoField(gettext('Company Name'), 'Company'),
        companySize: new MarketoSelectField(gettext('Company Size'), 'Employee_Tier__c', [
            "I'm a contractor / freelancer",
            '1–199',
            '200–999',
            '1,000–5,000',
            '5,001+',
        ]),
        companyEmployeeSize: new MarketoSelectField(
            gettext('Number of learners'),
            'Initial_Seat_Count__c',
            ['2,001+', '201 - 2,000', '51 - 200', '21 - 50', '11 - 20', '2 - 10', 'Just myself'],
        ),
        jobTitle: new MarketoField(gettext('Job Title'), 'Title'),
        jobLevel: new MarketoSelectField(gettext('Job Level'), 'jobLevelfromLead', [
            'C-Level',
            'VP',
            'Director / Sr',
            'Manager',
            'Individual Contributor',
        ]),
        trainingNeeds: new MarketoField(
            gettext("What are your organization's training needs? (Optional)"),
            'UFO_Comments__c',
            {},
            true,
        ),
    };
    udData: UDData;
    userType: string;

    constructor(udData: UDData) {
        this.udData = udData;
        this.formFields.email.addValidator(genericEmailValidator);
        this.formFields.email.addValidator(businessEmailValidator);
        this.formFields.phone.addValidator(phoneNumberValidator);
        this.userType = '';
        this.markRequiredFieldLabels();
    }

    initializeMarketoForm(mktoUrl: string, mktoMunchkin: string, udData?: UDData) {
        const formId = getFormIdByPriceCountry(udData?.Config?.price_country?.id);
        if (!udData || !formId) {
            return;
        }
        this.udData = udData;
        this.formId = formId;
        // Load MktoForms2 Munchkin
        marketoLoaders.loadMarketoForms2(() => {
            window.MktoForms2.loadForm(mktoUrl, mktoMunchkin, formId);
            window.MktoForms2.whenReady((form: MarketoForms2) => {
                this.setMarketoFormInstance(form);
                form.onSuccess(() => {
                    fireDemoRequestSuccessEvent(() => {
                        window.location.href = this.formRedirectUrl;
                        this.isFormSubmitting = false;
                    });
                    return false;
                });
            });
        });
    }

    getRefParam() {
        const searchParams = new URLSearchParams(
            (serverOrClient.global as Window)?.location?.search ?? '',
        );
        return searchParams.get('ref') ?? '';
    }

    markRequiredFieldLabels() {
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        Object.entries(this.formFields).forEach(([_, value]) => {
            if (
                (value instanceof MarketoField || value instanceof MarketoSelectField) &&
                !value.optional
            ) {
                value.label = value.label.concat(' *');
            }
        });
    }

    setOriginalReferrerCookie() {
        const originalReferrerCookie = Cookies.get('original_referrer');
        const refParam = this.getRefParam();
        const originalReferrer =
            originalReferrerCookie || (refParam && refParam !== '' ? refParam : '--empty--');
        if (!originalReferrerCookie && CookieConsent.allowsFunctionalCookies()) {
            // Expires in 1000 days per the behavior on business.udemy.com
            Cookies.set('original_referrer', originalReferrer, {expires: 1000});
        }
    }

    @action
    setMarketoFormInstance(form: MarketoForms2) {
        this.marketoForm = form;
        this.isMarketoFormLoaded = true;
    }

    setUserType(userType: string) {
        this.userType = userType;
    }

    @computed
    get isValid() {
        for (const field of Object.values(this.formFields)) {
            // optional fields don't need to be validated against null
            if (field.optional && field.value === null) {
                continue;
            }

            if (field.value === null || field.validationState === ERROR_STATES.ERROR) {
                return false;
            }
        }
        return true;
    }

    @action
    validate() {
        // Fields are "untouched" by default, this means their value is null. As soon as the user
        // starts filling in the fields, they become "touched" when changed.
        for (const field of Object.values(this.formFields)) {
            if (field.value === null && !field.optional) {
                field.value = '';
            }
        }
    }

    @computed
    get formRedirectUrl() {
        if (
            this.formFields.companyEmployeeSize &&
            this.formFields.companyEmployeeSize.value === '2 - 10'
        ) {
            return 'https://business.udemy.com/thank-you/?plan=team';
        }

        // Redirect users who select 'Just myself' to the personal plan thank you page
        return this.formFields.companyEmployeeSize.value === 'Just myself'
            ? 'https://business.udemy.com/thank-you/?plan=personal'
            : 'https://business.udemy.com/thank-you/';
    }

    attachQueryParameters(formValues: {[key: string]: string | null}, countryIso: string) {
        const searchParams = new URLSearchParams(window.location.search);

        // Checks each defined utm query parameter and assigns it to its Marketo field
        // If it doesn't exist, it assigns the Marketo field to ''
        for (const [utmQueryParam, mktoField] of Object.entries(UTM_QUERY_PARAM_MKTO_FIELDS)) {
            if (searchParams.has(utmQueryParam)) {
                formValues[mktoField] = searchParams.get(utmQueryParam);
                // Delete so they don't get sent again in the "query params" field
                searchParams.delete(utmQueryParam);
            } else {
                formValues[mktoField] = '';
            }
        }

        // MaxMind ISO code (2 char, not 3 like on udemy.com)
        formValues[UTM_MKTO_FIELDS.utmCountry] = countryIso;
        // Locale, only for IN/DE
        formValues[UTM_MKTO_FIELDS.utmLanguage] = ['IN', 'DE'].includes(countryIso)
            ? this.udData.request.locale
            : '';

        // Get referer from queryParams and remove it
        // --[state]-- is from business.udemy.com
        formValues[AUTO_MKTO_FIELDS.referrer] = searchParams.get('ref') ?? '--empty--';
        searchParams.delete('ref');

        // Don't track the redirect argument
        searchParams.delete(REDIRECT_QUERY_PARAM);

        // Join remaining query params
        formValues[AUTO_MKTO_FIELDS.queryParams] = searchParams.toString() || '--no-query-params--';
    }

    serialize() {
        const form: {[key: string]: string} = {};
        const countryIso = this.udData.Config.price_country?.id || '?';

        for (const field of Object.values(this.formFields)) {
            form[field.mktoName] = field.transformedValue || field.value;
        }

        form[AUTO_MKTO_FIELDS.uuid] = getVisitorUuid();
        form[AUTO_MKTO_FIELDS.country] = countryIso;
        form[AUTO_MKTO_FIELDS.userType] = this.userType;
        form[AUTO_MKTO_FIELDS.formFillUrl] = window.location.origin + window.location.pathname;
        // TODO: Ensure the language_simple_english_title is consistently available before geo-expansion
        form[AUTO_MKTO_FIELDS.contentLanguage] =
            this.udData.request.language_simple_english_title ?? 'English';

        this.attachQueryParameters(form, countryIso);

        // Assign unknown values an empty string
        for (const mktoKey of Object.values(UNKNOWN_MKTO_FIELDS)) {
            form[mktoKey] = '';
        }

        return form;
    }

    @autobind
    submit() {
        this.validate();

        if (!this.marketoForm || !this.isMarketoFormLoaded || !this.isValid) {
            return;
        }
        this.isFormSubmitting = true;

        const searchParams = new URLSearchParams(window.location.search);
        Tracker.publishEvent(
            new UFBFormActionClickEvent({
                componentName: 'MarketoFormSubmitButton',
                placement: this.getRefParam(),
                userType: this.userType,
                utmSource: searchParams.get(UTM_QUERY_PARAM_MKTO_FIELDS.utm_source) ?? '',
            }),
        );
        Tracker.publishEvent(
            new ExternalLeadFormSubmitEvent({
                formId: 'cta:demoRequestSuccess',
                isSuccessful: true,
            }),
        );

        // This adds certain hidden fields to the form which must be provided in order for the Marketo
        // form submission to be processed correctly downstream.
        this.marketoForm.addHiddenFields(this.serialize());
        this.marketoForm.submit();
    }
}
